import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { createMission, organisationMissions } from '../api/endpoints'
import type { Mission, Organisation, User } from '../api/types'
import { Toc } from './Toc'
import { MembersPanel } from '../components/MembersPanel'
import { NewEventForm } from '../components/NewEventForm'
import { StatusPill } from '../components/StatusPill'
import { AppHeader } from '../components/AppHeader'
import { Calendar, MapPin, Plus, Shield, Users } from '../components/Icons'

/**
 * An organisation's events, and the way into the TOC for one of them.
 *
 * Deliberately shows every state rather than only what is running. An organiser spends as much time
 * on next weekend's game and last weekend's after-action as on today's.
 */
export function Dashboard({
  user,
  organisation,
  onSwitchOrganisation,
  onSignOut,
}: {
  user: User
  organisation: Organisation
  onSwitchOrganisation: () => void
  onSignOut: () => void
}) {
  const [missions, setMissions] = useState<Mission[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [open, setOpen] = useState<Mission | null>(null)
  const [showMembers, setShowMembers] = useState(false)

  const load = useCallback(async () => {
    const result = await organisationMissions(organisation.id)
    if (result.ok) {
      setMissions(result.value)
      setError(null)
    } else {
      setError(result.error.message)
    }
  }, [organisation.id])

  useEffect(() => {
    void load()
  }, [load])

  const header = (crumbs?: ReactNode, actions?: ReactNode) => (
    <AppHeader
      user={user}
      organisation={organisation}
      crumbs={crumbs}
      actions={actions}
      onMembers={() => setShowMembers(true)}
      onSwitchOrganisation={onSwitchOrganisation}
      onSignOut={onSignOut}
    />
  )

  const members = showMembers && (
    <MembersPanel organisation={organisation} me={user} onClose={() => setShowMembers(false)} />
  )

  if (open) {
    return (
      <>
        <Toc
          mission={open}
          organisation={organisation}
          me={user}
          header={header}
          onClose={() => {
            setOpen(null)
            void load()
          }}
        />
        {members}
      </>
    )
  }

  const canRunEvents = organisation.myRole === 'OWNER' || organisation.myRole === 'MANAGER'

  // What an organiser looks for first: the game that is on, then the next one, then the record.
  const groups: { title: string; missions: Mission[] }[] = [
    { title: 'Running now', missions: (missions ?? []).filter((m) => m.status === 'ACTIVE') },
    {
      title: 'Upcoming',
      missions: (missions ?? [])
        .filter((m) => m.status === 'PLANNING')
        .sort((a, b) => (a.startsAt ?? '9').localeCompare(b.startsAt ?? '9')),
    },
    {
      title: 'Finished',
      missions: (missions ?? []).filter((m) => m.status === 'COMPLETED' || m.status === 'ARCHIVED'),
    },
  ]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {header()}

      <main style={{ flex: 1, overflow: 'auto' }}>
        <div className="page">
          <div className="page-title">
            <h1>Events</h1>
            {canRunEvents && (
              <button className="with-icon" onClick={() => setCreating(true)}>
                <Plus /> New event
              </button>
            )}
          </div>

          {error && <div className="banner danger">{error}</div>}

          {missions === null && !error && <p className="tertiary">Loading events…</p>}

          {missions?.length === 0 && (
            <div className="empty-state">
              <h3>No events yet</h3>
              <p>
                {canRunEvents
                  ? 'Create an event, then hand its join code or QR to the teams coming.'
                  : 'When a manager of this club puts an event on, it appears here.'}
              </p>
              {canRunEvents && (
                <button className="with-icon" onClick={() => setCreating(true)}>
                  <Plus /> New event
                </button>
              )}
            </div>
          )}

          {groups
            .filter((group) => group.missions.length > 0)
            .map((group) => (
              <section key={group.title}>
                <div className="section-head">
                  <h2>{group.title}</h2>
                  <span className="count">{group.missions.length}</span>
                </div>
                <div className="event-grid">
                  {group.missions.map((mission) => (
                    <EventCard key={mission.id} mission={mission} onOpen={() => setOpen(mission)} />
                  ))}
                </div>
              </section>
            ))}
        </div>
      </main>

      {members}

      {creating && (
        <NewEventForm
          onCancel={() => setCreating(false)}
          onCreate={async (body) => {
            const result = await createMission(organisation.id, body)
            if (result.ok) {
              setCreating(false)
              await load()
              // Straight into the TOC: somebody who just created an event wants its join code.
              setOpen(result.value)
            }
            return result
          }}
        />
      )}
    </div>
  )
}

function EventCard({ mission, onOpen }: { mission: Mission; onOpen: () => void }) {
  const live = mission.status === 'ACTIVE'
  return (
    <button className={`event-card${live ? ' live' : ''}`} onClick={onOpen}>
      <div className="spread" style={{ alignItems: 'flex-start' }}>
        <h3>{mission.name}</h3>
        <StatusPill status={mission.status} />
      </div>
      <div className="event-meta">
        {mission.startsAt && (
          <span>
            <Calendar /> {formatDate(mission.startsAt)}
          </span>
        )}
        {mission.locationName && (
          <span>
            <MapPin /> {mission.locationName}
          </span>
        )}
        <span>
          <Shield /> {mission.teamCount} team{mission.teamCount === 1 ? '' : 's'}
        </span>
        <span>
          <Users /> {mission.participantCount} player{mission.participantCount === 1 ? '' : 's'}
        </span>
      </div>
      <span className="open-hint">{live ? 'Open the TOC →' : 'Open →'}</span>
    </button>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
